/**
 * Eventos que o painel registra sozinho (docs/telemetria.md, seção 6):
 * `app.opened`, `page.viewed`, `extension.status_changed`, `error.client` e `error.api`.
 *
 * Separado do `index.ts` para o registro de uso não depender do roteador nem das stores.
 */
import type { createBrowserRouter } from 'react-router-dom';
import { setApiFailureReporter } from '../apiClient';
import { deleteLocal, getLocal, onExtensionMessage } from '../extensionBridge';
import { canEmit, type ActivityEventName } from './catalog';
import type { ExtensionConnection, ExtensionFlow } from '../extensionFlow';
import { useCaptureStore } from '../../stores/captureStore';
import { getRequestContext } from '../requestContext';
import { emitActivity, routeTemplate, setActivityRoute } from './index';
import { ACTIVITY_PATH } from './sender';

type AppRouter = ReturnType<typeof createBrowserRouter>;

/** O mesmo erro em menos de 10 s conta uma vez (um erro de render repete a cada tentativa). */
const ERROR_REPEAT_MS = 10 * 1000;
const recentErrors = new Map<string, number>();
/** A mesma rota da API falhando com o mesmo status conta uma vez a cada 30 s (telas consultam de novo). */
const API_REPEAT_MS = 30 * 1000;
const recentApiFailures = new Map<string, number>();

/** `true` quando a mesma chave já foi registrada dentro da janela. */
function repeated(seen: Map<string, number>, key: string, windowMs: number): boolean {
  const now = Date.now();
  const last = seen.get(key);
  if (last !== undefined && now - last < windowMs) return true;
  seen.set(key, now);
  if (seen.size > 200) seen.clear();
  return false;
}

type ClientErrorKind = 'render' | 'route' | 'unhandled' | 'rejection' | 'storage' | 'other';

export function reportClientError(kind: ClientErrorKind, error: unknown, code?: string): void {
  try {
    const message = error instanceof Error ? error.message : String(error ?? '');
    const stack = error instanceof Error ? (error.stack ?? '') : '';
    if (repeated(recentErrors, `${kind}|${message}`, ERROR_REPEAT_MS)) return;
    emitActivity('error.client', { kind, code: code?.slice(0, 64) }, undefined, {
      error: { message, stack },
    });
  } catch {
    /* noop */
  }
}

/**
 * Sinal da extensão (decisão 169: a extensão não chama o servidor, o painel registra).
 * Contrato: `{ id, name, props?, at, runId?, listId?, step? }`, só eventos com origem
 * `extension` no catálogo (CAPTCHA, travamento, atualização, erro na aba de captura).
 */
export function ingestExtensionSignal(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const signal = raw as Record<string, unknown>;
  const name = signal['name'];
  if (typeof name !== 'string' || !canEmit(name, 'extension')) return false;
  const props = signal['props'];
  const at = typeof signal['at'] === 'string' ? signal['at'] : undefined;
  emitActivity(
    name as ActivityEventName,
    props && typeof props === 'object' && !Array.isArray(props)
      ? (props as Record<string, unknown>)
      : undefined,
    {
      run: typeof signal['runId'] === 'string' ? signal['runId'] : undefined,
      search: typeof signal['listId'] === 'string' ? signal['listId'] : undefined,
      step: typeof signal['step'] === 'number' ? signal['step'] : undefined,
    },
    {
      surface: 'extension',
      ...(at ? { at } : {}),
      ...(typeof signal['id'] === 'string' ? { eventId: signal['id'] } : {}),
    },
  );
  return true;
}

/** Sinais que a extensão guardou com o painel fechado (`activitySignals`, até 500). */
export async function collectStoredSignals(): Promise<number> {
  try {
    const local = await getLocal<{ activitySignals?: unknown }>();
    const list = Array.isArray(local.activitySignals) ? local.activitySignals.slice(0, 500) : [];
    if (!list.length) return 0;
    let accepted = 0;
    for (const signal of list) if (ingestExtensionSignal(signal)) accepted += 1;
    // Recolher de novo por falha aqui não duplica: cada sinal tem id, e o servidor descarta repetidos.
    deleteLocal('activitySignals');
    return accepted;
  } catch {
    return 0;
  }
}

/** Rota da API como modelo, sem ids nem query string: `api/v2/lists/:id/leads`. */
export function endpointTemplate(path: string): string {
  return routeTemplate(path.startsWith('/') ? path : `/${path}`).slice(1, 121) || '/';
}

/** Modelo da rota a partir das rotas casadas do roteador (`/list/:module/:id/leads`). */
function templateOf(router: AppRouter): string {
  const parts = router.state.matches
    .map((match) => match.route.path)
    .filter((path): path is string => typeof path === 'string' && path.length > 0)
    .map((path) => path.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean);
  return parts.length ? `/${parts.join('/')}` : routeTemplate(router.state.location.pathname);
}

let installed = false;

export function installActivityObservers(router: AppRouter): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const bootStartedAt = typeof performance !== 'undefined' ? performance.now() : 0;

  let lastRoute = templateOf(router);
  setActivityRoute(lastRoute);
  emitActivity('app.opened', { loadMs: Math.max(0, Math.round(bootStartedAt)) });
  emitActivity('page.viewed');
  router.subscribe((state) => {
    if (state.navigation.state !== 'idle') return;
    const next = templateOf(router);
    if (next === lastRoute) return;
    const from = lastRoute;
    lastRoute = next;
    setActivityRoute(next);
    emitActivity('page.viewed', { from });
  });

  let lastState: string | null = null;
  const stateOf = (connection: ExtensionConnection, flow: ExtensionFlow | null): string => {
    if (connection === 'connected' && flow === 'legacy') return 'legacy';
    if (connection === 'refresh-required') return 'refresh_required';
    return connection;
  };
  useCaptureStore.subscribe((store) => {
    const state = stateOf(store.extensionConnection, store.extensionFlow);
    if (state === lastState) return;
    const previous = lastState;
    lastState = state;
    if (previous === null && state === 'checking') return;
    const version = getRequestContext().version;
    emitActivity('extension.status_changed', {
      state,
      version: version ? String(version).slice(0, 40) : undefined,
    });
    if (state === 'connected') void collectStoredSignals();
  });

  onExtensionMessage((data) => {
    if ('__activitySignal' in data) ingestExtensionSignal(data['__activitySignal']);
  });

  window.addEventListener('error', (event) =>
    reportClientError('unhandled', event.error ?? event.message),
  );
  window.addEventListener('unhandledrejection', (event) =>
    reportClientError('rejection', event.reason),
  );

  setApiFailureReporter((failure) => {
    // O próprio envio do registro de uso não gera evento: falhar aqui viraria um ciclo.
    if (failure.path.includes(ACTIVITY_PATH)) return;
    const endpoint = endpointTemplate(failure.path);
    if (repeated(recentApiFailures, `${endpoint}|${failure.status}`, API_REPEAT_MS)) return;
    emitActivity('error.api', {
      endpoint,
      status: failure.status,
      durationMs: Math.min(failure.durationMs, 600000),
      code: failure.code?.slice(0, 64),
    });
  });
}
